import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Zap, Wind, Sun, Waves, Ship, Globe, ArrowRight, CheckCircle2, Sparkles, Factory, Anchor } from 'lucide-react';
import { ContactDialog } from '../components/ContactDialog';

export const HomePage = () => {
  const { t, language } = useLanguage();
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactSource, setContactSource] = useState('');

  const openContact = (source) => {
    setContactSource(source);
    setContactDialogOpen(true);
  };

  return (
    <>
      <SEO 
        title={t('home.hero.title')}
        description={t('home.hero.text')}
        path="/"
      />

      {/* Hero Section - Split Images with Color Overlays */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
        data-testid="hero-section"
      >
        {/* Split Background Images - Paraguaná specific */}
        <div className="absolute inset-0 flex">
          {/* Left Half - Arid Peninsula Landscape */}
          <div className="w-1/2 relative">
            <img 
              src="https://images.unsplash.com/photo-1763845982629-6ef57c4d76ef?w=1920&q=85" 
              alt="Paraguaná arid coast" 
              className="w-full h-full object-cover"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-primary-600/85"></div>
          </div>
          {/* Right Half - Industrial/Refinery */}
          <div className="w-1/2 relative">
            <img 
              src="https://images.pexels.com/photos/9407356/pexels-photo-9407356.jpeg?w=1920&q=85" 
              alt="Industrial facilities" 
              className="w-full h-full object-cover"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-cyan-600/85"></div>
          </div>
        </div>
        
        {/* Additional gradient overlay for cohesion */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-700/40 via-transparent to-cyan-600/40"></div>
        
        {/* Animated Overlays */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-300/20 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
        </div>

        {/* Logo Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5">
          <img src="/logo.png" alt="" className="w-96 h-auto" loading="lazy" decoding="async" aria-hidden="true" />
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-5 py-2.5 bg-white/25 backdrop-blur-md rounded-full text-white text-sm font-semibold shadow-lg border border-white/20">
              <Sparkles className="w-4 h-4" />
              <span>{t('home.hero.subtitle')}</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight animate-fade-in drop-shadow-lg" data-testid="hero-title">
              {language === 'es' ? 'La Puerta Verde' : 'The Green Gateway'}
              <br />
              <span className="text-cyan-200">{language === 'es' ? 'del Caribe' : 'of the Caribbean'}</span>
            </h1>

            <p className="text-xl md:text-2xl text-white font-medium max-w-3xl mx-auto leading-relaxed drop-shadow-md" data-testid="hero-text">
              {language === 'es' 
                ? 'Convierte a Paraguaná en el hub líder de hidrógeno verde. Recursos renovables + infraestructura + ubicación estratégica.'
                : 'Transform Paraguaná into the leading green hydrogen hub. Renewable resources + infrastructure + strategic location.'}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button 
                size="lg" 
                className="bg-white text-primary-700 hover:bg-gray-50 text-lg py-7 shadow-2xl font-bold w-full sm:w-[280px] justify-center"
                onClick={() => openContact('hero-primary')}
                data-testid="hero-cta-dataroom"
              >
                <Zap className="w-5 h-5 mr-2" />
                {t('home.hero.ctaDataRoom')}
              </Button>
              <Button 
                size="lg" 
                className="bg-cyan-500 hover:bg-cyan-400 text-white text-lg py-7 shadow-2xl font-bold w-full sm:w-[280px] justify-center"
                onClick={() => openContact('hero-secondary')}
                data-testid="hero-cta-meeting"
              >
                {t('home.hero.ctaMeeting')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-12" data-testid="hero-stats">
              {[
                { value: '6.5', unit: 'kWh/m²', label: language === 'es' ? 'Solar' : 'Solar' },
                { value: '9.7', unit: 'm/s', label: language === 'es' ? 'Viento' : 'Wind' },
                { value: '5', unit: 'GW', label: language === 'es' ? 'Meta 2035' : '2035 Target' },
                { value: '3', unit: language === 'es' ? 'días' : 'days', label: language === 'es' ? 'A Miami' : 'To Miami' },
              ].map((stat, idx) => (
                <div key={idx} className="bg-white/20 backdrop-blur-md border border-white/30 p-5 rounded-2xl text-white shadow-xl">
                  <div className="text-3xl font-bold">{stat.value}<span className="text-lg text-cyan-200 ml-0.5">{stat.unit}</span></div>
                  <div className="text-sm text-white/90 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Vision Showcase - Full bleed integrated vision */}
      <section className="relative overflow-hidden" data-testid="vision-showcase-section">
        <div className="relative">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_358a3cc3-6a48-4bbe-b4a3-0219bc98f8e2/artifacts/fwnbqzod_ChatGPT%20Image%20Aug%2030%2C%202026%2C%2011_52_42%20PM.png"
            alt={language === 'es' ? 'Visión integrada de Paraguaná como hub verde' : 'Integrated vision of Paraguaná as green hub'}
            className="w-full h-[70vh] md:h-[85vh] object-cover"
            loading="lazy"
            decoding="async"
          />
          {/* Bottom gradient for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/25 to-transparent"></div>
          {/* Right-side subtle color wash on brand */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-primary-900/20"></div>

          {/* Content */}
          <div className="absolute inset-x-0 bottom-0 pb-10 md:pb-16">
            <div className="container mx-auto">
              <div className="max-w-3xl space-y-5">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5" />
                  {t('home.visionShowcase.eyebrow')}
                </div>
                <h2
                  className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-lg"
                  data-testid="vision-title"
                >
                  {t('home.visionShowcase.title')}
                </h2>
                <p className="text-base md:text-lg text-white/90 max-w-2xl leading-relaxed drop-shadow">
                  {t('home.visionShowcase.text')}
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {t('home.visionShowcase.chips').map((chip, idx) => {
                    const icons = [Sun, Factory, Anchor, Zap];
                    const Icon = icons[idx] || Sparkles;
                    return (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/12 backdrop-blur-md border border-white/25 text-white/95 text-xs md:text-sm font-medium"
                        data-testid={`vision-chip-${idx}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {chip}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Paraguaná - Renewable Energy Focus */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="why-section">
        {/* Logo Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5 pointer-events-none">
          <img src="/logo.png" alt="" className="w-[700px] h-auto" loading="lazy" decoding="async" aria-hidden="true" />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4 pt-4">
            <h2 className="text-4xl md:text-5xl font-bold gradient-text" data-testid="why-title">
              {language === 'es' ? '¿Por qué Paraguaná?' : 'Why Paraguaná?'}
            </h2>
            <p className="text-xl text-slate-700 font-medium">
              {language === 'es' 
                ? 'Tres ventajas clave que hacen de Paraguaná el lugar ideal'
                : 'Three key advantages that make Paraguaná the ideal location'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Sun,
                title: language === 'es' ? 'Recursos Abundantes' : 'Abundant Resources',
                description: language === 'es' 
                  ? 'Costa árida + sol intenso ideal para energía solar y eólica'
                  : 'Arid coast + intense sun ideal for solar and wind energy',
                color: 'text-yellow-600',
                image: 'https://images.unsplash.com/photo-1763845982597-b3b8997bd89d?w=600&h=400&fit=crop&q=80'
              },
              {
                icon: Wind,
                title: language === 'es' ? 'Energía Eólica' : 'Wind Energy',
                description: language === 'es'
                  ? 'Vientos constantes para generación continua de energía limpia'
                  : 'Constant winds for continuous clean energy generation',
                color: 'text-cyan-600',
                image: 'https://images.pexels.com/photos/34527086/pexels-photo-34527086.jpeg?w=600&h=400&fit=crop&q=80'
              },
              {
                icon: Ship,
                title: language === 'es' ? 'Infraestructura H₂' : 'H₂ Infrastructure',
                description: language === 'es'
                  ? 'Capacidad portuaria para exportación de hidrógeno verde a mercados globales'
                  : 'Port capacity for green hydrogen export to global markets',
                color: 'text-primary-600',
                image: 'https://images.pexels.com/photos/15970023/pexels-photo-15970023.jpeg?w=600&h=400&fit=crop&q=80'
              },
            ].map((item, idx) => (
              <Card 
                key={idx} 
                className="overflow-hidden border-2 border-slate-100 hover:border-primary-300 hover:shadow-2xl transition-all duration-300 group"
                data-testid={`why-card-${idx}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute top-4 left-4 p-3 bg-white rounded-xl shadow-lg">
                    <item.icon className={`w-7 h-7 ${item.color}`} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-700 leading-relaxed">{item.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Energy Potential - Data Visualization */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-cyan-50/40 relative overflow-hidden" data-testid="energy-section">
        {/* Logo Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5 pointer-events-none">
          <img src="/logo.png" alt="" className="w-[600px] h-auto" loading="lazy" decoding="async" aria-hidden="true" />
        </div>
        
        {/* Additional small logos in corners */}
        <div className="absolute top-10 right-10 opacity-5 pointer-events-none">
          <img src="/logo.png" alt="" className="w-32 h-auto" loading="lazy" decoding="async" aria-hidden="true" />
        </div>
        <div className="absolute bottom-10 left-10 opacity-5 pointer-events-none">
          <img src="/logo.png" alt="" className="w-32 h-auto" loading="lazy" decoding="async" aria-hidden="true" />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900" data-testid="energy-title">
                {language === 'es' ? 'Potencial Energético' : 'Energy Potential'}
              </h2>
              <p className="text-xl text-slate-700 font-medium">
                {language === 'es'
                  ? 'Recursos naturales abundantes para producción a escala'
                  : 'Abundant natural resources for large-scale production'}
              </p>
            </div>

            {/* Energy Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {[
                { 
                  icon: Sun, 
                  label: 'Solar', 
                  value: '~6.5',
                  unit: 'kWh/m²/día',
                  gradient: 'from-amber-400 to-orange-500',
                  bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50'
                },
                { 
                  icon: Wind, 
                  label: language === 'es' ? 'Viento' : 'Wind', 
                  value: '~9.7',
                  unit: 'm/s',
                  gradient: 'from-blue-500 to-cyan-500',
                  bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50'
                },
                { 
                  icon: Waves, 
                  label: language === 'es' ? 'Oleaje' : 'Wave', 
                  value: '~2.5-4.0',
                  unit: 'm',
                  gradient: 'from-cyan-500 to-teal-500',
                  bgColor: 'bg-gradient-to-br from-cyan-50 to-teal-50'
                },
              ].map((item, idx) => (
                <Card 
                  key={idx} 
                  className={`${item.bgColor} border-0 p-8 hover:scale-105 transition-transform duration-300 shadow-lg`}
                  data-testid={`energy-card-${idx}`}
                >
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${item.gradient} mb-4 shadow-md`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-sm font-semibold text-slate-700 mb-2">{item.label}</div>
                  <div className="text-4xl font-bold text-slate-900 mb-1" data-testid={`energy-value-${idx}`}>
                    {item.value}
                  </div>
                  <div className="text-sm text-slate-600 font-medium">{item.unit}</div>
                </Card>
              ))}
            </div>

            {/* Benefits */}
            <Card className="p-8 bg-white border-2 border-primary-200 shadow-lg">
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-7 h-7 text-primary-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {language === 'es' ? 'Generación Complementaria 24/7' : '24/7 Complementary Generation'}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {language === 'es'
                      ? 'La combinación de sol durante el día y viento constante permite operación continua y mayor utilización de electrolizadores.'
                      : 'The combination of daytime sun and constant wind enables continuous operation and higher electrolyzer utilization.'}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Coast Showcase - Full bleed coast with molecular overlay */}
      <section className="relative overflow-hidden" data-testid="coast-showcase-section">
        <div className="relative">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_358a3cc3-6a48-4bbe-b4a3-0219bc98f8e2/artifacts/i8nl3nnn_ChatGPT%20Image%20Aug%2030%2C%202026%2C%2011_53_02%20PM.png"
            alt={language === 'es' ? 'Costa de Paraguaná con red de conexión H2' : 'Paraguaná coast with H2 connectivity network'}
            className="w-full h-[65vh] md:h-[80vh] object-cover"
            loading="lazy"
            decoding="async"
          />
          {/* Left-side dark gradient for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-900/40 to-transparent"></div>

          {/* Content */}
          <div className="absolute inset-y-0 left-0 flex items-center">
            <div className="container mx-auto">
              <div className="max-w-2xl space-y-5">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-400/20 backdrop-blur-md border border-cyan-300/40 text-cyan-100 text-xs font-semibold uppercase tracking-wider">
                  <Globe className="w-3.5 h-3.5" />
                  {t('home.coastShowcase.eyebrow')}
                </div>
                <h2
                  className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-lg"
                  data-testid="coast-title"
                >
                  {t('home.coastShowcase.title')}
                </h2>
                <p className="text-base md:text-lg text-white/90 leading-relaxed drop-shadow">
                  {t('home.coastShowcase.text')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scale & Routes - Timeline */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="scale-section">
        {/* Logo Watermark top right */}
        <div className="absolute top-20 right-20 opacity-5 pointer-events-none">
          <img src="/logo.png" alt="" className="w-64 h-auto" loading="lazy" decoding="async" aria-hidden="true" />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
                {language === 'es' ? 'Escala & Conectividad' : 'Scale & Connectivity'}
              </h2>
              <p className="text-xl text-slate-700 font-medium">
                {language === 'es'
                  ? 'Crecimiento modular con acceso directo a mercados clave'
                  : 'Modular growth with direct access to key markets'}
              </p>
            </div>

            {/* Phase Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {[
                { phase: '2026-28', capacity: '100 MW', production: '15K t/año' },
                { phase: '2029-31', capacity: '1 GW', production: '150K t/año' },
                { phase: '2035', capacity: '5 GW', production: '750K t/año' },
              ].map((item, idx) => (
                <Card 
                  key={idx}
                  className="p-7 text-center border-2 border-primary-300 hover:border-primary-500 hover:shadow-xl transition-all bg-gradient-to-br from-white to-primary-50/30"
                  data-testid={`phase-card-${idx}`}
                >
                  <div className="text-sm font-bold text-primary-700 mb-2">{item.phase}</div>
                  <div className="text-4xl font-bold text-slate-900 mb-2">{item.capacity}</div>
                  <div className="text-slate-700 font-medium">{item.production}</div>
                </Card>
              ))}
            </div>

            {/* Routes */}
            <Card className="p-8 bg-gradient-to-br from-cyan-50 to-teal-50 border-2 border-cyan-200 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-slate-900">
                  {language === 'es' ? 'Rutas Marítimas' : 'Maritime Routes'}
                </h3>
                <Ship className="w-10 h-10 text-primary-600" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { destination: 'Cartagena', time: '~1 día' },
                  { destination: 'Miami', time: '~3 días' },
                  { destination: 'Rotterdam', time: '~9-10 días' },
                ].map((route, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/60 p-4 rounded-lg">
                    <span className="font-bold text-slate-900">{route.destination}</span>
                    <span className="text-primary-700 font-bold">{route.time}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA - Paraguaná Focus */}
      <section className="py-24 relative overflow-hidden" data-testid="final-cta-section">
        {/* Background Image - H2 Plant at Night */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_358a3cc3-6a48-4bbe-b4a3-0219bc98f8e2/artifacts/x2kgxwnk_ChatGPT%20Image%20Aug%2030%2C%202026%2C%2011_52_52%20PM.png" 
            alt="Green hydrogen plant" 
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-primary-900/70 to-cyan-900/80"></div>
        </div>
        
        {/* Logo Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5">
          <img src="/logo.png" alt="" className="w-96 h-auto" loading="lazy" decoding="async" aria-hidden="true" />
        </div>
        
        <div className="relative container mx-auto px-4 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg" data-testid="final-cta-title">
              {language === 'es' 
                ? 'Únete al Futuro del Hidrógeno Verde'
                : 'Join the Green Hydrogen Future'}
            </h2>
            <p className="text-xl text-white font-medium drop-shadow-md">
              {language === 'es'
                ? 'Inversores, compradores y socios tecnológicos: hablemos sobre offtake, alianzas e inversión.'
                : 'Investors, offtakers and technology partners: let\'s talk about offtake, partnerships and investment.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                className="bg-white text-primary-700 hover:bg-gray-50 text-lg px-12 py-7 shadow-2xl font-bold"
                onClick={() => openContact('final-cta')}
                data-testid="final-cta-button"
              >
                <Globe className="w-5 h-5 mr-2" />
                {t('home.finalCta.cta')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Dialog */}
      <ContactDialog 
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        source={contactSource}
      />
    </>
  );
};
